import React, { useEffect, useRef, useState } from 'react';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { Columns, Column, Block } from '@safelyq/bulma-ui-library';
import { Navbar, AdminDrawer } from '@safelyq/base-feature';
import { useAdminCount } from 'hooks/useAdminCount';
import { setMessageCount, setNotificationCount, setUnreadReviewCount, setIsBusinessActive } from 'Redux/BusinessSlice/Business';
import { setDashboardDataChanged } from 'Redux/MiscSlice/Misc';

//for firestore
import firestore from '@firebase';
import { COLLECTION } from 'constants/index';
import { useDispatch, useSelector } from 'react-redux';
import useScreenWidth from '../../../CustomHookComponent/RenderingOnScreenSize';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { useHistory } from 'react-router-dom';

export const AdminLayout = ({ ...props }) => {

  const { userinfo: userInfo } = useSelector((state) => state?.auth);
  const isSidebarCollapsed = useSelector((state) => state?.misc?.isSidebarCollapsed);
  const history = useHistory();
  const businessApprovalStateGlobal = useSelector((state) => state?.business?.businessApprovalStatus);

  const dispatch = useDispatch();
  const {
    children,
    isAllowSideBar,
    isAllowFullScreen = false,
    isAllowNotifications,
    businessId,
    ...rest
  } = props;


  // redirect to the businesses list if business is shutdown and user is not superadmin
  // we use email to check if the data is fetched from api
  useEffect(() => {
    if (!!businessApprovalStateGlobal && userInfo?.email && !userInfo?.isSuperAdmin) {
      if (businessApprovalStateGlobal === "PendingShutdown" || businessApprovalStateGlobal === "PermanentShutdown") {
        history.push("/admin/businesses");
      }
    }
  }, [businessApprovalStateGlobal, userInfo, history]);

  // Ref to track if API was called than dont call api again when other snapshot is triggered
  const apiCalled = useRef(false);
  const [toggle, setToggle] = useState(false); // mobile version navbar toggle

  // Component will only on the screens desktop or greater
  const screenWidth = useScreenWidth();
  const isDesktopOrGreater = screenWidth > 768;

  const handle = useFullScreenHandle();

  const handleFullScreen = () => {
    handle.enter();
  };

  // Admin notifications and review counts api
  const {
    onGetAdminData,
    unreadCount,
    unreadReviews,
    isActive,
    notificationsUnviewedCount,
  } = useAdminCount(apiCalled);

  // Set Unread message count to global state
  useEffect(() => {
    dispatch(setMessageCount(unreadCount));
    dispatch(setDashboardDataChanged(true));
  }, [unreadCount])

  // Set Unread review count to global state
  useEffect(() => {
    dispatch(setUnreadReviewCount(unreadReviews));
    dispatch(setDashboardDataChanged(true));
  }, [unreadReviews])

  // Set Unviewed notification count to global state
  useEffect(() => {
    dispatch(setNotificationCount(notificationsUnviewedCount));
    dispatch(setDashboardDataChanged(true));
  }, [notificationsUnviewedCount])

  // Set IsBusinessActive to global state
  useEffect(() => {
    if (isActive !== null) {
      dispatch(setIsBusinessActive(isActive));
    }
  }, [isActive])

  // Collection will be triggered with respective value change
  useEffect(() => {

    // Chat Listner
    const unsubscribeChat = onSnapshot(
      query(
        collection(firestore, COLLECTION.CHAT),
        where('businessId', '==', `${businessId}`)
      ),
      async () => {
        if (!apiCalled.current) {
          apiCalled.current = true;
          onGetAdminData({ variables: { id: businessId, showAll: true, businessId } }); // fetch admin data
        }
      }
    );

    // Notification listner
    const unsubscribeNotifications = onSnapshot(
      query(
        collection(firestore, COLLECTION.NOTIFICATIONS),
        where('businessId', '==', `${businessId}`)
      ),
      async () => {
        if (!apiCalled.current) {
          apiCalled.current = true;
          onGetAdminData({ variables: { id: businessId, showAll: true, businessId } });
        }
      }
    )

    // Comments listener
    const unsubscribeComments = onSnapshot(
      query(
        collection(firestore, COLLECTION.COMMENTS),
        where('businessId', '==', `${businessId}`)
      ),
      async () => {
        if (!apiCalled.current) {
          apiCalled.current = true;
          onGetAdminData({ variables: { id: businessId, showAll: true, businessId } });
        }
      }
    );

    // Appointments listener
    const unsubscribeAppointments = onSnapshot(
      query(
        collection(firestore, COLLECTION.APPOINTMENTS),
        where('businessId', '==', `${businessId}`)
      ),
      async () => {
        if (!apiCalled.current) {
          apiCalled.current = true;
          onGetAdminData({ variables: { id: businessId, showAll: true, businessId } });
        }
      }
    );

    // Ratings listener
    const unsubscribeRatings = onSnapshot(
      query(
        collection(firestore, COLLECTION.RATINGS),
        where('businessId', '==', `${businessId}`)
      ),
      async () => {
        if (!apiCalled.current) {
          apiCalled.current = true;
          onGetAdminData({ variables: { id: businessId, showAll: true, businessId } });
        }
      }
    );

    return () => {
      unsubscribeChat();
      unsubscribeNotifications();
      unsubscribeComments();
      unsubscribeAppointments();
      unsubscribeRatings();
    };
  }, [businessId]);

  const { avatarName } = rest;

  return (
    <Columns multiline gap='0' className='mb-0'>
      <Column size='12'>
        <Navbar
          onFullScreen={handleFullScreen}
          isAllowFullScreen={isAllowFullScreen}
          isAllowSideBar={isAllowSideBar}
          isAllowNotifications={isAllowNotifications}
          avatarName={avatarName}
          toggle={toggle}
          setToggle={setToggle}
        />
      </Column>
      {isAllowSideBar && isDesktopOrGreater && (
        <Column style={{ width: isSidebarCollapsed ? '70px' : '16.666667%', flex: 'none', transition: 'width 0.3s ease' }} className='is-hidden-mobile'>
          <AdminDrawer />
        </Column>
      )}
      <Column style={{ width: (isAllowSideBar && isDesktopOrGreater) ? (isSidebarCollapsed ? 'calc(100% - 70px)' : '83.333333%') : '100%', flex: 'none', transition: 'width 0.3s ease' }}>
        <FullScreen handle={handle}>
          <section className='hero p-0 is-white is-fullheight-with-navbar has-text-black'>
            <Block>{children}</Block>
          </section>
        </FullScreen>
      </Column>
    </Columns>
  );
};
